/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-08
 */

// Add third-party dependencies.
import { Avatar, Skeleton } from '@mui/material';
import { Person } from '@mui/icons-material';

// Add local dependencies.
import { getSafeUserInitial } from '../../utils/security';
import type { User } from '../../features/auth/authApi';
import { API_BASE_URL } from '../../config/api';

/**
 * Props for the UserAvatar component.
 */
interface UserAvatarProps {
  /**
   * User object containing avatar and name information.
   */
  user?: User | null;
  /**
   * Size of the avatar.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  /**
   * Whether to show a loading skeleton.
   * @default false
   */
  loading?: boolean;
  /**
   * Custom alt text for the avatar image.
   */
  alt?: string;
  /**
   * Additional CSS classes.
   */
  className?: string;
  /**
   * Click handler for the avatar.
   */
  onClick?: () => void;
  /**
   * Whether the avatar should be clickable (shows pointer cursor).
   * @default false
   */
  clickable?: boolean;
}

/**
 * UserAvatar component that displays user profile photos with fallback to initials.
 * Supports different sizes, loading states, and click interactions.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The user avatar component
 *
 * @example
 * // Basic usage
 * <UserAvatar user={user} />
 *
 * @example
 * // Large clickable avatar
 * <UserAvatar
 *   user={user}
 *   size="large"
 *   clickable
 *   onClick={() => setEditMode(true)}
 * />
 *
 * @example
 * // Loading state
 * <UserAvatar loading size="medium" />
 */
const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = 'medium',
  loading = false,
  alt,
  className,
  onClick,
  clickable = false,
}) => {
  /**
   * Gets the size dimensions for the avatar based on the size prop.
   */
  const getSizeDimensions = () => {
    switch (size) {
      case 'small':
        return { width: 32, height: 32, fontSize: '0.875rem' };
      case 'medium':
        return { width: 40, height: 40, fontSize: '1rem' };
      case 'large':
        return { width: 64, height: 64, fontSize: '1.5rem' };
      case 'xlarge':
        return { width: 96, height: 96, fontSize: '2rem' };
      default:
        return { width: 40, height: 40, fontSize: '1rem' };
    }
  };

  /**
   * Gets the alt text for the avatar image.
   */
  const getAltText = (): string => {
    if (alt) return alt;
    if (user?.first_name) {
      return `${user.first_name}${user.last_name ? ` ${user.last_name}` : ''} avatar`;
    }
    if (user?.email) {
      return `${user.email} avatar`;
    }
    return 'User avatar';
  };

  /**
   * Gets the initials to display when no avatar is available.
   */
  const getInitials = (): string => {
    if (!user) return '';
    return getSafeUserInitial(user.first_name, user.email);
  };

  const dimensions = getSizeDimensions();

  // Show loading skeleton
  if (loading) {
    return (
      <Skeleton
        variant="circular"
        width={dimensions.width}
        height={dimensions.height}
        className={className}
      />
    );
  }

  // Show default avatar if no user
  if (!user) {
    return (
      <Avatar
        sx={{
          width: dimensions.width,
          height: dimensions.height,
          bgcolor: 'grey.400',
          cursor: clickable || onClick ? 'pointer' : 'default',
        }}
        className={className}
        onClick={onClick}
      >
        <Person sx={{ fontSize: dimensions.fontSize }} />
      </Avatar>
    );
  }

  /**
   * Gets the full avatar URL by prepending the API base URL if needed.
   */
  const getAvatarUrl = (): string | undefined => {
    if (!user.has_avatar || !user.avatar_url) {
      return undefined;
    }

    // If the avatar_url is already a full URL (starts with http), use it as-is
    if (user.avatar_url.startsWith('http')) {
      return user.avatar_url;
    }

    // If it's a relative URL, prepend the API base URL
    return `${API_BASE_URL}${user.avatar_url}`;
  };

  // Show user avatar with image or initials
  return (
    <Avatar
      src={getAvatarUrl()}
      alt={getAltText()}
      sx={{
        width: dimensions.width,
        height: dimensions.height,
        bgcolor: 'grey.600',
        color: 'white',
        fontSize: dimensions.fontSize,
        cursor: clickable || onClick ? 'pointer' : 'default',
        '&:hover': {
          ...(clickable || onClick
            ? {
                opacity: 0.8,
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out',
              }
            : {}),
        },
      }}
      className={className}
      onClick={onClick}
    >
      {!user.has_avatar || !user.avatar_url ? getInitials() : undefined}
    </Avatar>
  );
};

export default UserAvatar;
